import { useEffect, useRef, useState } from 'react';

import { useGetGoodTariffsNums } from '@/api';
import {
    GoodTariffsResListItem,
    NumberResponseItem,
    StandardResponseItem,
    TariffResponseItem,
} from '@/api/types';
import { RightContent } from '@/components/AllNumbers/TariffsList/TariffCard/RightContent/RightContent.tsx';
import { Loader } from '@/components/Loader/Loader.tsx';
import { Collapsible } from '@/components/shared/Collapsible/Collapsible.tsx';
import { NumberCard } from '@/components/shared/NumberCard/NumberCard.tsx';
import { TariffOptionsList } from '@/components/shared/TariffOptionsList/TariffOptionsList.tsx';
import { useAuthStore, useModalStore } from '@/store';
import { ChosenNumber } from '@/types';

import cn from './styles.module.sass';

interface ITariffCardProps {
    title?: string;
    description?: string;
    price?: string;
    deviceType?: string;
    numbers?: NumberResponseItem[];
    options?: TariffResponseItem['options'];
    tariffOptions?: StandardResponseItem[];
    onClick: (chosenNumber: Partial<ChosenNumber>) => void;
    chosenObj: Partial<Omit<ChosenNumber, 'number' | 'region'>>;
    tariffId: string;
    tariffsList?: GoodTariffsResListItem[];
    showLoadMore?: boolean;
}

export const TariffCard = ({
    title,
    description,
    price,
    deviceType,
    numbers,
    options,
    tariffOptions,
    onClick,
    chosenObj,
    tariffId,
    tariffsList,
    showLoadMore,
}: ITariffCardProps) => {
    const [tariffNumbers, setTariffNumbers] = useState(numbers);
    const [currentPage, setCurrentPage] = useState(0);
    const prevPage = useRef(currentPage);
    const { data, isLoading } = useGetGoodTariffsNums(currentPage, tariffId, '');
    const { isAuth } = useAuthStore();
    const { setIsOpen, setShowSignUp } = useModalStore();

    const handleLoadMore = () => {
        setCurrentPage(currentPage + 1);
    };

    const handleClick = () => {
        setShowSignUp(false);
        setIsOpen(true);
    };

    useEffect(() => {
        if (tariffNumbers && data && prevPage.current !== currentPage) {
            prevPage.current = currentPage;
            setTariffNumbers(tariffNumbers.concat(data));
        }
    }, [data]);

    return (
        <Collapsible
            rightContent={<RightContent price={price} deviceType={deviceType} />}
            operator={chosenObj.operator}
            title={title}
            isTariffTitle
            onClick={isAuth ? undefined : handleClick}
            isExpandable={isAuth}
            description={description}
        >
            <div className={cn.contentContainer}>
                <TariffOptionsList options={options} tariffOptions={tariffOptions} />
                <div className={cn.numbersList}>
                    {tariffNumbers?.map(number => {
                        const numTariffs = tariffsList
                            ?.map(item => item.tariff)
                            .filter(t => number.tariffs.includes(t.sid))
                            .map(t => t.privatePrice);

                        const minTariffPrice = numTariffs ? Math.min.apply(null, numTariffs) : 0;

                        return (
                            <NumberCard
                                key={number.msisdn}
                                number={number.msisdn}
                                region={number.region.name}
                                onClick={onClick}
                                chosenObj={chosenObj}
                                minTariffPrice={minTariffPrice}
                                tariffs={number.tariffs}
                                tariffId={tariffId}
                            />
                        );
                    })}
                </div>
                {showLoadMore && (
                    <div onClick={handleLoadMore} className={cn.loadMore}>
                        {isLoading ? <Loader mini /> : showLoadMore}
                    </div>
                )}
            </div>
        </Collapsible>
    );
};
